import { Fragment, useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  DEFAULT_RAM_STAGE_CORRECTION,
  PARAMETER_LIST,
  PARAMETERS,
  SLURRY_CHANNELS,
  SLURRY_MANUAL_KINDS,
  ramStageRatio,
  type AnalysisPoint,
  type ParameterGroup,
  type ParameterId,
  type ParameterSeries,
  type PipeChange,
  type SecondaryAnalysis,
  type SecondaryResponse,
  type SlurryChannelId,
  type SlurryManualKind,
  type SlurryManualReading,
} from "@tbm/shared";
import clsx from "clsx";
import { api } from "../lib/api";
import { trackFeature } from "../lib/analytics";
import {
  usePreprocessingParams,
  useBulkingMa,
  type BulkingMaSettings,
} from "../state/preprocessing";
import { PlotCard } from "../components/PlotCard";
import { localNaiveInputToUtcIso, useTimezone, utcToLocalNaive } from "../lib/datetime";
import { usePersistentState } from "../lib/usePersistentState";

type Range = "1h" | "8h" | "24h" | "7d" | "drive" | "custom";
type XAxis = "time" | "chainage";

const RANGES: { id: Range; label: string }[] = [
  { id: "1h",     label: "1h" },
  { id: "8h",     label: "8h" },
  { id: "24h",    label: "24h" },
  { id: "7d",     label: "7d" },
  { id: "drive",  label: "Drive" },
  { id: "custom", label: "Custom" },
];

const RELATIVE: Record<Exclude<Range, "drive" | "custom">, string> = {
  "1h":  "-1h",
  "8h":  "-8h",
  "24h": "-24h",
  "7d":  "-7d",
};

/**
 * Refetch cadence per range. Wide windows carry big payloads (the
 * server auto-downsamples them, but 7d/Drive responses are still the
 * largest), and re-downloading + re-rendering the whole drive every
 * 20 s is what made the Drive view feel glitchy. New data only lands
 * every ~30 s anyway (fetcher cadence), and at drive scale a 2-minute
 * refresh is visually indistinguishable.
 */
const REFETCH_MS: Record<Range, number> = {
  "1h":   20_000,
  "8h":   20_000,
  "24h":  20_000,
  "7d":   60_000,
  drive:  120_000,
  custom: 60_000,
};

/** Subtitle label shown when the server auto-downsampled a raw request. */
const AGG_LABELS: Record<string, string> = {
  "10s": "10 s",
  "1m":  "1 min",
  "5m":  "5 min",
  "1h":  "1 h",
  "1d":  "1 d",
};

/** "2026-08-01" → "2026-08-02". Calendar-day arithmetic, tz-agnostic. */
function nextDay(dateStr: string): string {
  return new Date(Date.parse(`${dateStr}T00:00:00Z`) + 86_400_000)
    .toISOString()
    .slice(0, 10);
}

/**
 * Hard cap on simultaneous y-axes. Plotly will render more, but axis
 * titles start clipping past 4 on a typical desktop card width.
 */
const MAX_SLOTS = 4;

/**
 * Slots that count as "forces" (kN). When 2 or more of these are
 * selected at once, they collapse onto a single shared y-axis
 * labelled "Force (kN)" in black; individual trace colours stay so
 * the legend still distinguishes them. A single force keeps its own
 * coloured axis (existing behaviour).
 */
const FORCE_SLOT_IDS: ReadonlySet<string> = new Set<string>([
  "jackingForce",
  "totalScForce",
  "derived:faceForce",
  "derived:skinForce",
  "derived:cutterheadForce",
  "derived:resolvedWeight",
]);

function isForceSlot(slot: SlotId): boolean {
  return FORCE_SLOT_IDS.has(slot);
}

/** Pipe-change overlay colours — chainage axis uses a single neutral
 *  red; time axis uses green for "Pipe start" and red for "Pipe end". */
const PIPE_CHANGE_COLOR = "#c8102e";
const PIPE_START_COLOR = "#16a34a";
const PIPE_END_COLOR = "#c8102e";

// ─── Group definitions ────────────────────────────────────────────
// Some parameters share a unit and read better when overlaid on a
// single y-axis ("Line P feed/slurry", "Flow feed/return", "SC1–4").
// A group is one slot in the chip row and on the plot — so a group
// counts as 1 against MAX_SLOTS even though it draws multiple traces.
const PARAM_GROUPS = {
  linePressures: {
    label: "Line Pressures",
    unit: "bar",
    ids: ["feedLinePressure", "slurryLinePressure"] as ParameterId[],
    traceLabels: ["Feed", "Slurry"],
  },
  flowRates: {
    label: "Flow Rates",
    unit: "m³/h",
    ids: ["feedFlowRate", "returnFlowRate"] as ParameterId[],
    traceLabels: ["Feed", "Return"],
  },
  steeringCylinders: {
    label: "SCs",
    unit: "bar",
    ids: ["sc1", "sc2", "sc3", "sc4"] as ParameterId[],
    traceLabels: ["SC 1", "SC 2", "SC 3", "SC 4"],
  },
} as const;

/**
 * Some single-parameter chips want a different label from the global
 * `shortName` (which is used in compact contexts like the Dashboard
 * hero cells). Keep shortName tight for those, override here for
 * chip + axis labelling in this page only.
 */
const CHIP_LABEL_OVERRIDES: Partial<Record<ParameterId, string>> = {
  facePressure: "Face Pressure",
  totalScForce: "Total SC Force",
};
type GroupId = keyof typeof PARAM_GROUPS;

const GROUPED_PARAM_IDS = new Set<ParameterId>(
  Object.values(PARAM_GROUPS).flatMap((g) => g.ids),
);

// ─── Jack slots ───────────────────────────────────────────────────
// Debug chips for understanding the jack-station data. The
// shaft mask experiment didn't work (V15_DNR_Z values don't match the
// expected 1-6 range), so these chips just surface the raw signals
// instead of trying to mask anything — the operator can read the
// station value off the chart and tell us which numbers correspond
// to the shaft vs interjacks.
const JACK_SLOTS = {
  activeStation: {
    label: "Active Station",
    unit: "",
    // single trace: V15_DNR_Z (active jack-station integer). Reads 0
    // when only the launch-shaft is pushing; non-zero when an interjack
    // is selected.
  },
} as const;
type JackId = keyof typeof JACK_SLOTS;

// ─── Derived (formerly Secondary) analyses ─────────────────────────
// The dedicated Secondary Parameters page was retired — Bulking
// Factor and Skin Friction now live here as opt-in chips backed by
// /api/secondary. Each derived chip is one slot (one y-axis), with a
// single trace rendered as marker dots like the rest of the
// pulse-style sensors.
const DERIVED_SLOTS = {
  bulkingFactor: {
    analysis: "bulking_factor" as SecondaryAnalysis,
    label: "Bulking Factor",
    unit: "",
    /** What ends up next to the trace name in the legend. */
    traceLabel: "Bulking Factor",
  },
  skinFriction: {
    analysis: "skin_friction" as SecondaryAnalysis,
    label: "Skin Friction",
    unit: "kPa",
    traceLabel: "Skin Friction",
  },
  faceForce: {
    analysis: "face_force" as SecondaryAnalysis,
    label: "Face Force",
    unit: "kN",
    traceLabel: "Face Force",
  },
  skinForce: {
    analysis: "skin_force" as SecondaryAnalysis,
    label: "Skin Force",
    unit: "kN",
    traceLabel: "Skin Force",
  },
  cutterheadForce: {
    analysis: "cutterhead_force" as SecondaryAnalysis,
    label: "Cutterhead Force",
    unit: "kN",
    traceLabel: "Cutterhead Force",
  },
  resolvedWeight: {
    analysis: "resolved_weight" as SecondaryAnalysis,
    label: "Resolved Weight",
    unit: "kN",
    traceLabel: "Resolved Weight",
  },
} as const;
type DerivedId = keyof typeof DERIVED_SLOTS;

// A slot is either a single parameter, a parameter group, a derived
// analysis, a jack slot, or a slurry monitoring channel (/api/slurry). Stored as strings so they round-trip
// through usePersistentState's JSON-friendly store.
type SlotId =
  | ParameterId
  | `group:${GroupId}`
  | `derived:${DerivedId}`
  | `jack:${JackId}`
  | `slurry:${SlurryChannelId}`;

function isGroupSlot(slot: SlotId): slot is `group:${GroupId}` {
  return slot.startsWith("group:");
}
function isDerivedSlot(slot: SlotId): slot is `derived:${DerivedId}` {
  return slot.startsWith("derived:");
}
function isJackSlot(slot: SlotId): slot is `jack:${JackId}` {
  return slot.startsWith("jack:");
}
function isSlurrySlot(slot: SlotId): slot is `slurry:${SlurryChannelId}` {
  return slot.startsWith("slurry:");
}
function groupOf(slot: SlotId): GroupId | null {
  return isGroupSlot(slot) ? (slot.slice(6) as GroupId) : null;
}
function derivedOf(slot: SlotId): DerivedId | null {
  return isDerivedSlot(slot) ? (slot.slice(8) as DerivedId) : null;
}
function jackOf(slot: SlotId): JackId | null {
  return isJackSlot(slot) ? (slot.slice(5) as JackId) : null;
}
function slurryOf(slot: SlotId): SlurryChannelId | null {
  return isSlurrySlot(slot) ? (slot.slice(7) as SlurryChannelId) : null;
}
/**
 * Parameter ids each slot needs from /api/series. Jack slots pull
 * activeJackStation + either jackingForce (force slots) or the 16
 * ramDispNN ids (ram slots) — the mask is applied later, in the
 * trace-build loop.
 */
function slotParamIds(slot: SlotId): ParameterId[] {
  if (isDerivedSlot(slot) || isSlurrySlot(slot)) return [];
  const j = jackOf(slot);
  if (j === "activeStation") return ["activeJackStation"];
  const g = groupOf(slot);
  return g ? [...PARAM_GROUPS[g].ids] : [slot as ParameterId];
}
function slotShortLabel(slot: SlotId): string {
  const j = jackOf(slot);
  if (j) return JACK_SLOTS[j].label;
  const d = derivedOf(slot);
  if (d) return DERIVED_SLOTS[d].label;
  const sc = slurryOf(slot);
  if (sc) return SLURRY_CHANNELS[sc].label;
  const g = groupOf(slot);
  if (g) return PARAM_GROUPS[g].label;
  const paramId = slot as ParameterId;
  return CHIP_LABEL_OVERRIDES[paramId] ?? PARAMETERS[paramId].shortName;
}
function slotUnit(slot: SlotId): string {
  const j = jackOf(slot);
  if (j) return JACK_SLOTS[j].unit;
  const d = derivedOf(slot);
  if (d) return DERIVED_SLOTS[d].unit;
  const sc = slurryOf(slot);
  if (sc) return SLURRY_CHANNELS[sc].unit;
  const g = groupOf(slot);
  return g ? PARAM_GROUPS[g].unit : PARAMETERS[slot as ParameterId].unit;
}
/** How many traces the slot will draw — drives the colour family slot count. */
function slotTraceCount(slot: SlotId): number {
  if (isDerivedSlot(slot)) return 1;
  if (isJackSlot(slot)) return 1;
  if (isSlurrySlot(slot)) return 1;
  const g = groupOf(slot);
  return g ? PARAM_GROUPS[g].ids.length : 1;
}

// ─── Colour families ──────────────────────────────────────────────
// Each slot picks a family. Inside a family, traces use the dark/dark/
// light/light shades (for groups with multiple traces) and the axis
// uses a midline colour that visually sits between them. For single-
// trace slots, both the trace and the axis use the family's midline so
// the trace and its axis match exactly.
type ColorFamily = {
  /** Dark → light. Used by groups; index 0 is darkest. */
  shades: [string, string, string, string];
  /** Midline colour used for the axis (and for single-trace slots). */
  axis: string;
};
const FAMILIES: ColorFamily[] = [
  // wb red
  { shades: ["#7d0a1d", "#a50d24", "#e7475a", "#f48b97"], axis: "#c8102e" },
  // blue
  { shades: ["#1e3a8a", "#1d4ed8", "#60a5fa", "#93c5fd"], axis: "#2563eb" },
  // green
  { shades: ["#14532d", "#15803d", "#4ade80", "#86efac"], axis: "#16a34a" },
  // amber
  { shades: ["#78350f", "#b45309", "#fbbf24", "#fcd34d"], axis: "#d97706" },
];

/** Mix a #rrggbb colour toward black — manual slurry readings use a
 *  darker shade of their trace's colour so they stand out on it. */
function darken(hex: string, amount = 0.35): string {
  const n = parseInt(hex.slice(1), 16);
  const f = (c: number) => Math.round(c * (1 - amount)).toString(16).padStart(2, "0");
  return `#${f((n >> 16) & 255)}${f((n >> 8) & 255)}${f(n & 255)}`;
}

function escapeHover(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function colorsFor(
  slotIndex: number,
  traceCount: number,
): { traces: string[]; axis: string } {
  const family = FAMILIES[slotIndex % FAMILIES.length] ?? FAMILIES[0]!;
  if (traceCount <= 1) {
    return { traces: [family.axis], axis: family.axis };
  }
  if (traceCount === 2) {
    return { traces: [family.shades[1], family.shades[2]], axis: family.axis };
  }
  if (traceCount === 3) {
    return {
      traces: [family.shades[0], family.shades[1], family.shades[2]],
      axis: family.axis,
    };
  }
  return {
    traces: [
      family.shades[0],
      family.shades[1],
      family.shades[2],
      family.shades[3],
    ],
    axis: family.axis,
  };
}

// ─── Selectable chip list, split into four categories ────────────
// Categories drive the visual layout: columns separated by
// vertical lines, with widths proportional to chip count so the
// largest group gets the most space. Membership is derived from
// PARAMETER_LIST groups + the PARAM_GROUPS / DERIVED_SLOTS / JACK_SLOTS
// keys, so adding a new parameter automatically lands in the right
// column based on its `group` in packages/shared/src/parameters.ts.
const MEASURED_GROUPS: ParameterGroup[] = ["drive", "pressure", "flow", "steering"];
const STATUS_GROUPS: ParameterGroup[] = ["status", "machine"];

const SLOT_CATEGORIES: Array<{ label: string; slots: SlotId[] }> = [
  {
    label: "Measured parameters",
    slots: [
      ...PARAMETER_LIST
        .filter(
          (p) => MEASURED_GROUPS.includes(p.group) && !GROUPED_PARAM_IDS.has(p.id),
        )
        .map((p) => p.id as SlotId),
      ...(Object.keys(PARAM_GROUPS) as GroupId[]).map(
        (g) => `group:${g}` as SlotId,
      ),
    ],
  },
  {
    label: "Status & health",
    slots: [
      ...PARAMETER_LIST
        .filter(
          (p) => STATUS_GROUPS.includes(p.group) && !GROUPED_PARAM_IDS.has(p.id),
        )
        .map((p) => p.id as SlotId),
      ...(Object.keys(JACK_SLOTS) as JackId[]).map(
        (j) => `jack:${j}` as SlotId,
      ),
    ],
  },
  {
    label: "Secondary parameters",
    slots: (Object.keys(DERIVED_SLOTS) as DerivedId[]).map(
      (d) => `derived:${d}` as SlotId,
    ),
  },
  {
    label: "Slurry Monitoring Parameters",
    slots: (Object.keys(SLURRY_CHANNELS) as SlurryChannelId[]).map(
      (c) => `slurry:${c}` as SlotId,
    ),
  },
];

const DEFAULT_SLOTS: SlotId[] = ["chainage", "advanceRate"];

// Slurry-only view (/slurry, SLURRY_PASSWORD login): chainage and advance
// rate plus the slurry channels. The backend refuses anything else for
// that session, so these are the only chips it can offer.
const SLURRY_VIEW_CATEGORIES: Array<{ label: string; slots: SlotId[] }> = [
  { label: "Drive", slots: ["chainage", "advanceRate"] },
  SLOT_CATEGORIES.find((c) => c.label === "Slurry Monitoring Parameters")!,
];
const SLURRY_VIEW_SLOTS = new Set(SLURRY_VIEW_CATEGORIES.flatMap((c) => c.slots));
const SLURRY_VIEW_DEFAULT_SLOTS: SlotId[] = [
  "advanceRate",
  "slurry:liveViscosity",
  "slurry:yieldPoint",
  "slurry:density",
];

/** One rendered y-axis. `shared` marks the collapsed "Force (kN)" axis. */
interface AxisGroup {
  slots: SlotId[];
  shared: boolean;
}

/**
 * Parameters that should render as semi-transparent marker dots rather
 * than connected lines — matches the "Measured JF" style on the JF
 * prediction page (JFPlot.tsx: type scattergl, mode markers, size 4,
 * marker opacity 0.55). Used for sensors where every sample is a
 * discrete event and a connecting line implies false continuity (the
 * cylinders pulse, so a line jumps wildly between low and high values).
 */
const DOT_PARAMS = new Set<ParameterId>([
  "jackingForce",
  "totalScForce",
  "advanceRate",
  "torque",
  "facePressure",
  "oilTempContainer",
  "oilTempMachine",
  "coolingWaterTemp",
]);

// ─── Parameter definitions ───────────────────────────────────────
// Prose pulled (where applicable) verbatim from the thesis,
// Table 3.3. Surfaces beneath the plot for whichever chips are
// currently active so an operator unfamiliar with a derived metric
// can see what it means without leaving the page.

/** Tiny inline math renderer: turns `X_y` and `X_{yz}` into
 *  X<sub>y</sub> / X<sub>yz</sub>. Avoids pulling in a full math
 *  library for what amounts to two equations. */
function renderMath(s: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let buf = "";
  let i = 0;
  while (i < s.length) {
    if (s[i] === "_") {
      if (buf.length > 0) { parts.push(buf); buf = ""; }
      if (s[i + 1] === "{") {
        const close = s.indexOf("}", i + 2);
        if (close === -1) { buf += s[i]!; i++; continue; }
        parts.push(<sub key={`${i}-b`}>{s.slice(i + 2, close)}</sub>);
        i = close + 1;
      } else if (s[i + 1] !== undefined) {
        parts.push(<sub key={`${i}-s`}>{s[i + 1]!}</sub>);
        i += 2;
      } else {
        buf += s[i]!;
        i++;
      }
    } else {
      buf += s[i]!;
      i++;
    }
  }
  if (buf.length > 0) parts.push(buf);
  return <>{parts}</>;
}

/** Per-single-parameter prose. Group members (Feed/Slurry pressure,
 *  Feed/Return flow, SC 1–4) get their definitions through the group
 *  table below since they don't appear as standalone chips. */
const SINGLE_DEFS: Partial<Record<ParameterId, string>> = {
  chainage:     "Distance between the launch shaft inside wall and the TBM face.",
  advanceRate:  "Speed of the TBM and pipe string.",
  rpm:          "Rotational speed of the cutterhead.",
  torque:       "Rotational force applied to rotate the cutterhead.",
  jackingForce: "Force required to advance the TBM and pipe string.",
  totalScForce: "Sum of forces from the steering cylinders.",
  facePressure: "Pressure exerted at the tunnel face to support the ground.",
  bypassStatus: "Bypass-valve state: 2 = closed (mining), 1 = open; any other value is an error state.",
  oilTempContainer: "Hydraulic oil temperature in the container tank.",
  oilTempMachine: "Hydraulic oil temperature in the machine tank.",
  coolingWaterTemp: "Cooling water temperature in the machine cooling circuit.",
  oilTempStatus: "Tank monitoring oil temperature status. 0 = no fault, 1 = warning, 2 = fault.",
};

const JACK_DEFS: Record<JackId, string> = {
  activeStation:
    "Active jack-station integer (V15_DNR_Z). Reads 0 when only the launch-shaft is pushing; non-zero values name the active interjack station.",
};

/** Per-group prose. When the group draws multiple traces (line
 *  pressures, flow rates) each trace gets its own one-liner so the
 *  reader can disambiguate Feed vs Slurry / Return. */
const GROUP_DEFS: Record<
  GroupId,
  { short?: string; traces?: { label: string; meaning: string }[] }
> = {
  linePressures: {
    traces: [
      { label: "Feed",   meaning: "Pressure in the feed slurry line before the TBM face." },
      { label: "Slurry", meaning: "Pressure in the slurry return line after the TBM face." },
    ],
  },
  flowRates: {
    traces: [
      { label: "Feed",   meaning: "Flow rate of slurry pumped to the TBM face." },
      { label: "Return", meaning: "Flow rate of slurry pumped away from the TBM face." },
    ],
  },
  steeringCylinders: {
    short: "Hydraulic pressure in the cylinders used to steer the TBM.",
  },
};

/** Per-derived-metric definition: an equation, its variable list, and
 *  any caveats the operator should be aware of when reading the trace. */
const DERIVED_DEFS: Record<
  DerivedId,
  {
    equation: string;
    variables: { sym: string; meaning: string }[];
    notes?: string[];
  }
> = {
  bulkingFactor: {
    equation: "BF = (Q_r − Q_f) / (AR · A · b)",
    variables: [
      { sym: "Q_r", meaning: "return-line flow rate" },
      { sym: "Q_f", meaning: "feed-line flow rate" },
      { sym: "AR",  meaning: "advance rate" },
      { sym: "A",   meaning: "face area, with the diameter taken as the pipe outside diameter" },
      { sym: "b",   meaning: "bulking-factor baseline from the preprocessing settings (1.0 by default)" },
    ],
    notes: [
      "Lubricant/grout fills annular gap.",
      "BF > 1 indicates over-excavation. 1% over-excavation corresponds to BF = 1.01.",
      "Bentonite lubrication from ports near the cutterhead, or groundwater taken on at the face, may also inflate the measured flow difference and so increase BF.",
    ],
  },
  skinFriction: {
    equation: "SF = (F_J − F_{SC}) / (π · D · L)",
    variables: [
      { sym: "F_J",     meaning: "jacking force at the shaft" },
      { sym: "F_{SC}",  meaning: "total steering-cylinder force at the face" },
      { sym: "D",       meaning: "pipe outside diameter" },
      { sym: "L",       meaning: "current chainage" },
    ],
    notes: [
      "Note that resultant horizontal force resolved from the weight of the pipe string and TBM due to driving uphill or downhill will be built in to the skin force/friction component.",
    ],
  },
  faceForce: {
    equation: "F_{face} = A · P_{face}",
    variables: [
      { sym: "A",        meaning: "face area" },
      { sym: "P_{face}", meaning: "face pressure" },
    ],
  },
  skinForce: {
    equation: "F_{skin} = F_J − F_{SC}",
    variables: [
      { sym: "F_J",    meaning: "jacking force at the shaft" },
      { sym: "F_{SC}", meaning: "total steering-cylinder force" },
    ],
    notes: [
      "Note that resultant horizontal force resolved from the weight of the pipe string and TBM due to driving uphill or downhill will be built in to the skin force/friction component.",
    ],
  },
  cutterheadForce: {
    equation: "F_{ch} = F_{SC} − P_{face} · A",
    variables: [
      { sym: "F_{SC}",   meaning: "total steering-cylinder force" },
      { sym: "P_{face}", meaning: "face pressure" },
      { sym: "A",        meaning: "face area" },
    ],
    notes: [
      "Resisting force to bury and turn the cutterhead.",
    ],
  },
  resolvedWeight: {
    equation: "F_{rw}(x) = W_{cut} · sin(θ(x)) + ∫_{x−L_{tbm}}^{x} (W_{tbm}/L_{tbm}) · sin(θ(s)) ds + ∫_{0}^{x−L_{tbm}} w_{pipe} · sin(θ(s)) ds",
    variables: [
      { sym: "W_{cut}",   meaning: "cutterhead weight (kN)" },
      { sym: "W_{tbm}",   meaning: "TBM body weight (kN)" },
      { sym: "L_{tbm}",   meaning: "TBM body length (m)" },
      { sym: "w_{pipe}",  meaning: "pipe-string weight per metre (kN/m)" },
      { sym: "θ(s)",      meaning: "drive inclination at chainage s, from the piecewise-constant incline profile" },
      { sym: "x",         meaning: "current chainage" },
    ],
    notes: [
      "Horizontal weight component of the pipe string + TBM + cutterhead resolved against the drive inclination. Positive when pushing uphill (adds to the jacking load); negative when pushing downhill (assists).",
    ],
  },
};

/**
 * Return the definition pane content for a derived chip, swapping the
 * Skin Force / Skin Friction variants when the incline (resolved-weight)
 * feature is enabled — the formulas then explicitly subtract Frw and
 * the uphill/downhill caveat no longer applies.
 */
function derivedDefFor(
  id: DerivedId,
  inclineEnabled: boolean,
): { equation: string; variables: { sym: string; meaning: string }[]; notes?: string[] } {
  if (!inclineEnabled) return DERIVED_DEFS[id];
  if (id === "skinForce") {
    return {
      equation: "F_{skin} = F_J − F_{SC} − F_{rw}",
      variables: [
        { sym: "F_J",    meaning: "jacking force at the shaft" },
        { sym: "F_{SC}", meaning: "total steering-cylinder force" },
        { sym: "F_{rw}", meaning: "resolved weight" },
      ],
    };
  }
  if (id === "skinFriction") {
    return {
      equation: "SF = (F_J − F_{SC} − F_{rw}) / (π · D · L)",
      variables: [
        { sym: "F_J",     meaning: "jacking force at the shaft" },
        { sym: "F_{SC}",  meaning: "total steering-cylinder force at the face" },
        { sym: "F_{rw}",  meaning: "resolved weight" },
        { sym: "D",       meaning: "pipe outside diameter" },
        { sym: "L",       meaning: "current chainage" },
      ],
    };
  }
  return DERIVED_DEFS[id];
}

export function DriveParameters({ variant = "full" }: { variant?: "full" | "slurry" }) {
  const slurryView = variant === "slurry";
  // Persistent state keys are "drive.*" — old "primary.*" entries from
  // before the rename are ignored, so users land on defaults the first
  // time they hit the renamed page after deploy.
  const [range, setRange] = usePersistentState<Range>("drive.range", "24h");
  const [xAxis, setXAxis] = usePersistentState<XAxis>("drive.xAxis", "time");
  const [storedSlots, setSelectedSlots] = usePersistentState<SlotId[]>(
    slurryView ? "slurry.selectedSlots.v2" : "drive.selectedSlots",
    slurryView ? SLURRY_VIEW_DEFAULT_SLOTS : DEFAULT_SLOTS,
  );
  // A slurry session can't fetch anything outside its chip set; drop any
  // stale selection rather than firing requests that will be refused.
  const selectedSlots = useMemo(
    () => (slurryView ? storedSlots.filter((s) => SLURRY_VIEW_SLOTS.has(s)) : storedSlots),
    [slurryView, storedSlots],
  );
  // Pipe-change overlay. Toggle defaults to on. Pipe changes come from
  // the pipeChanges list already attached to /api/series — no extra fetch.
  const [showPipeChanges, setShowPipeChanges] = usePersistentState<boolean>(
    "drive.showPipeChanges",
    true,
  );
  // When 2+ force parameters are plotted they collapse onto one shared
  // black "Force (kN)" axis. This toggle lets the operator opt out and
  // give each force its own axis. Defaults on (the established
  // behaviour). Only meaningful with ≥2 forces selected — the toggle is
  // greyed out otherwise.
  const [shareForceAxisPref, setShareForceAxisPref] = usePersistentState<boolean>(
    "drive.shareForceAxis",
    true,
  );

  // Custom window: a start–end pair so the operator can inspect an old
  // stretch of the drive without pulling the entire drive's data into
  // the plot. In time mode the pair is calendar dates (wall-clock in
  // the drive's timezone); in chainage mode it's metres of chainage.
  // Switching the x-axis converts one into the other — see
  // setXAxisTracked below.
  const [customFrom, setCustomFrom] = usePersistentState<string>(
    "drive.customFrom",
    "",
  );
  const [customTo, setCustomTo] = usePersistentState<string>(
    "drive.customTo",
    "",
  );
  const [customFromCh, setCustomFromCh] = usePersistentState<string>(
    "drive.customFromCh",
    "",
  );
  const [customToCh, setCustomToCh] = usePersistentState<string>(
    "drive.customToCh",
    "",
  );
  const chainageWindow = range === "custom" && xAxis === "chainage";
  // Values persisted by the earlier datetime-local UI may carry a time
  // part; the selector is date-only now.
  const customFromDate = customFrom.slice(0, 10);
  const customToDate = customTo.slice(0, 10);
  const parseCh = (s: string): number | undefined => {
    if (s === "") return undefined;
    const n = Number(s);
    return Number.isFinite(n) ? n : undefined;
  };
  const customFromChNum = parseCh(customFromCh);
  const customToChNum = parseCh(customToCh);

  const pp = usePreprocessingParams();
  const ma = useBulkingMa();
  const tz = useTimezone();
  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });
  const from =
    range === "custom"
      ? !chainageWindow && customFromDate
        ? localNaiveInputToUtcIso(`${customFromDate}T00:00`, tz)
        : undefined
      : range === "drive"
        ? drive.data?.startDate
        : RELATIVE[range];
  // End of the To date's day (start of the next day, drive-timezone).
  // Only ever set for a custom time window; every other range ends now.
  const to =
    range === "custom" && !chainageWindow && customToDate
      ? localNaiveInputToUtcIso(`${nextDay(customToDate)}T00:00`, tz)
      : undefined;
  // Enough input to fetch? Chainage windows only need a start chainage;
  // everything else needs a resolved `from`.
  const windowReady = chainageWindow ? customFromChNum != null : !!from;

  // Ram-stage correction is a per-drive setting (also fed to the JF
  // prediction); read it from the drive record so the plot matches.
  // Declared up here so `ids` below can request chainage when the
  // correction is on.
  const ramStage = drive.data?.ramStageCorrection ?? DEFAULT_RAM_STAGE_CORRECTION;

  // Hide the Resolved Weight chip from the row when the incline feature
  // is disabled. The slot itself stays in DERIVED_SLOTS so the rest of
  // the trace pipeline is total; only the chip-row presentation cares.
  const inclineEnabled = drive.data?.incline?.enabled === true;
  const visibleCategories = useMemo(() => {
    if (slurryView) return SLURRY_VIEW_CATEGORIES;
    if (inclineEnabled) return SLOT_CATEGORIES;
    return SLOT_CATEGORIES.map((cat) => ({
      ...cat,
      slots: cat.slots.filter((s) => s !== "derived:resolvedWeight"),
    }));
  }, [inclineEnabled, slurryView]);

  // Flatten slots into the parameter ids the /api/series call needs.
  // Always include chainage when chainage is the x axis (so the
  // chainage map below isn't empty even if chainage isn't a y trace),
  // and when ram-stage correction is enabled — the JF trace handler
  // needs a per-sample chainage to decide which stage each sample is
  // in. Derived slots contribute zero ids — they have their own fetch.
  const ids = useMemo<ParameterId[]>(() => {
    const set = new Set<ParameterId>();
    for (const slot of selectedSlots) for (const id of slotParamIds(slot)) set.add(id);
    // Custom range always fetches chainage so an axis switch can convert
    // the window between dates and chainage (see setXAxisTracked).
    if (xAxis === "chainage" || ramStage.enabled || range === "custom") set.add("chainage");
    return [...set];
  }, [selectedSlots, xAxis, ramStage.enabled, range]);

  // A fully-bounded historical time window can't gain new data — skip
  // the periodic refetch entirely rather than re-downloading a static
  // slice. (Chainage windows keep refetching: an end chainage ahead of
  // the TBM fills in as the drive advances.)
  const refetchMs = range === "custom" && to ? false : REFETCH_MS[range];

  const seriesQuery = useQuery({
    queryKey: ["series-drive", ids, range, from, to, chainageWindow, customFromChNum, customToChNum, pp],
    queryFn: () =>
      api.series({
        ids,
        from: chainageWindow ? undefined : from!,
        to,
        fromChainage: chainageWindow ? customFromChNum : undefined,
        toChainage: chainageWindow ? customToChNum : undefined,
        preprocessing: pp,
      }),
    enabled: windowReady && ids.length > 0,
    refetchInterval: refetchMs,
    // Keep the previous window's traces on screen while a new range /
    // chip selection loads, instead of blanking the plot.
    placeholderData: keepPreviousData,
  });

  // Active derived analyses (in slot order, capped at MAX_SLOTS).
  // Used both for the parallel /api/secondary fetch and for trace
  // construction below.
  const activeDerived = useMemo<SecondaryAnalysis[]>(() => {
    const out: SecondaryAnalysis[] = [];
    for (const slot of selectedSlots.slice(0, MAX_SLOTS)) {
      const d = derivedOf(slot);
      if (d) out.push(DERIVED_SLOTS[d].analysis);
    }
    return out;
  }, [selectedSlots]);

  // One query for *all* active derived analyses, fanned out via
  // Promise.all so the hook count stays stable regardless of which
  // derived chips are selected. Result keyed by analysis name.
  const derivedQuery = useQuery({
    queryKey: ["derived-drive", activeDerived, range, from, to, chainageWindow, customFromChNum, customToChNum, pp],
    queryFn: async () => {
      const responses = await Promise.all(
        activeDerived.map((analysis) =>
          api.secondary({
            analysis,
            from: chainageWindow ? undefined : from!,
            to,
            fromChainage: chainageWindow ? customFromChNum : undefined,
            toChainage: chainageWindow ? customToChNum : undefined,
            preprocessing: pp,
          }),
        ),
      );
      const map = new Map<SecondaryAnalysis, SecondaryResponse>();
      activeDerived.forEach((a, i) => {
        const r = responses[i];
        if (r) map.set(a, r);
      });
      return map;
    },
    enabled: windowReady && activeDerived.length > 0,
    refetchInterval: refetchMs === false ? false : Math.max(30_000, refetchMs),
    placeholderData: keepPreviousData,
  });

  // Slurry channels come from the slurry PC log (/api/slurry/series), its
  // own table with its own timestamps. A chainage window has no time
  // bounds of its own, so it borrows them from the loaded chainage series.
  const activeSlurry = useMemo<SlurryChannelId[]>(() => {
    const out: SlurryChannelId[] = [];
    for (const slot of selectedSlots.slice(0, MAX_SLOTS)) {
      const c = slurryOf(slot);
      if (c) out.push(c);
    }
    return out;
  }, [selectedSlots]);
  const chainagePts = seriesQuery.data?.series.find((s) => s.parameterId === "chainage")?.points;
  const slurryFrom = chainageWindow ? chainagePts?.[0]?.t : from;
  const slurryTo = chainageWindow ? chainagePts?.[chainagePts.length - 1]?.t : to;
  // Manual readings (entered on /slurry) overlay the slurry channel they
  // belong to. Each kind draws on the first of its channels that's on
  // screen, so plastic viscosity / Marsh land on Live Viscosity, or on
  // Averaged Viscosity when only that one is selected.
  const manualSlotOf = useMemo(() => {
    const m = new Map<SlurryManualKind, SlurryChannelId>();
    for (const k of Object.keys(SLURRY_MANUAL_KINDS) as SlurryManualKind[]) {
      const c = (SLURRY_MANUAL_KINDS[k].channels as readonly SlurryChannelId[]).find((ch) =>
        activeSlurry.includes(ch),
      );
      if (c) m.set(k, c);
    }
    return m;
  }, [activeSlurry]);
  const manualQuery = useQuery({
    queryKey: ["slurry-manual", slurryFrom, slurryTo],
    queryFn: () => api.slurryManual.list({ from: slurryFrom, to: slurryTo }),
    enabled: manualSlotOf.size > 0 && !!slurryFrom,
    refetchInterval: refetchMs === false ? false : Math.max(60_000, refetchMs),
    placeholderData: keepPreviousData,
  });

  const slurryQuery = useQuery({
    queryKey: ["slurry-drive", activeSlurry, slurryFrom, slurryTo],
    queryFn: () => api.slurrySeries({ channels: activeSlurry, from: slurryFrom, to: slurryTo }),
    enabled: activeSlurry.length > 0 && !!slurryFrom,
    refetchInterval: refetchMs === false ? false : Math.max(60_000, refetchMs),
    placeholderData: keepPreviousData,
  });

  const seriesById = useMemo(() => {
    const map = new Map<ParameterId, ParameterSeries>();
    for (const s of seriesQuery.data?.series ?? []) map.set(s.parameterId, s);
    return map;
  }, [seriesQuery.data]);

  /**
   * Chainage at a given sample timestamp.
   *
   * This was an exact-timestamp Map lookup, which only worked because
   * every sensor happens to share one 250 ms scan clock. Any sensor
   * whose sample lands on a different instant — a different scan rate, a
   * dropped sample, a coarser bucket for one series — returned
   * `undefined`, and the two callers failed silently and differently:
   * the chainage x-axis plotted `null` (an invisible trace) and
   * `ramStageRatio(null, …)` returned 1, quietly disabling the
   * multistage-ram correction on the Jacking Force trace while the JF
   * page still applied it. Chainage is a monotonic position signal, so
   * the last known value at or before the sample is the right answer.
   */
  const chainageAt = useMemo(() => {
    const pts = seriesById.get("chainage")?.points ?? [];
    const times: number[] = [];
    const vals: number[] = [];
    for (const p of pts) {
      if (p.v == null) continue;
      const t = Date.parse(p.t);
      if (!Number.isFinite(t)) continue;
      times.push(t);
      vals.push(p.v);
    }
    return (tIso: string): number | null => {
      if (times.length === 0) return null;
      const t = Date.parse(tIso);
      if (!Number.isFinite(t)) return null;
      let lo = 0;
      let hi = times.length - 1;
      let best = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (times[mid]! <= t) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      // Before the first chainage sample in the window, the head of the
      // series is the closest thing to a real position.
      return best >= 0 ? vals[best]! : vals[0]!;
    };
  }, [seriesById]);

  // Pipe changes — attached server-side to /api/series. Used by the
  // overlay below and by the ram-stage Jacking Force correction.
  const pipeChanges = useMemo(
    () => seriesQuery.data?.pipeChanges ?? [],
    [seriesQuery.data],
  );

  const toggleSlot = (slot: SlotId) => {
    setSelectedSlots((prev) => {
      const adding = !prev.includes(slot);
      trackFeature(`drive_chip_${adding ? "add" : "remove"}_${slot}`, "drive");
      if (prev.includes(slot)) return prev.filter((s) => s !== slot);
      if (prev.length >= MAX_SLOTS) return prev;
      return [...prev, slot];
    });
  };
  const setXAxisTracked = (a: XAxis) => {
    trackFeature(`drive_xaxis_${a}`, "drive");
    // Carry a custom window across the axis switch: same stretch of
    // drive, re-expressed in the other unit from the loaded chainage
    // series (first/last chainage sample in the current window). A
    // blank end stays blank — "up to now" and "up to current chainage"
    // are the same live window.
    if (range === "custom" && a !== xAxis) {
      const pts = (seriesById.get("chainage")?.points ?? []).filter(
        (p) => p.v != null,
      );
      const first = pts[0];
      const last = pts[pts.length - 1];
      if (a === "chainage") {
        if (first) setCustomFromCh(first.v!.toFixed(1));
        setCustomToCh(customToDate && last ? last.v!.toFixed(1) : "");
      } else {
        if (first) {
          const d = utcToLocalNaive(first.t, tz);
          if (d) setCustomFrom(d.slice(0, 10));
        }
        const dTo =
          customToCh !== "" && last ? utcToLocalNaive(last.t, tz) : null;
        setCustomTo(dTo ? dTo.slice(0, 10) : "");
      }
    }
    setXAxis(a);
  };
  const setRangeTracked = (r: Range) => {
    trackFeature(`drive_range_${r}`, "drive");
    setRange(r);
  };

  // ── Build traces and per-axis config ────────────────────────────
  // Most slots become one y-axis. Exception: when 2 or more "force"
  // slots are selected together they share a single y-axis labelled
  // "Force (kN)" in black, so the operator can compare jacking, face,
  // skin and cutterhead components on the same scale. A single
  // selected force keeps its own coloured axis (existing behaviour).
  //
  // Memoised as one unit so the trace memo below has stable inputs —
  // rebuilding these arrays on every render made its dependency array
  // change every time and defeated the memo entirely.
  //
  // axisGroups: one entry per rendered y-axis. Build it by walking
  // visibleSlots in selection order; the first time a force is seen
  // (when shareForceAxis is true) creates the shared group and
  // subsequent forces fold into it. Non-force slots always get their
  // own group. axisIdxBySlot then tells every trace which y-axis it
  // belongs to (replacing the old slotIdx-based mapping).
  const {
    visibleSlots,
    slotColors,
    shareForceAxis,
    forceSelectedCount,
    axisGroups,
    axisIdxBySlot,
  } = useMemo(() => {
    const slots = selectedSlots.slice(0, MAX_SLOTS);
    const colors = slots.map((slot, slotIdx) =>
      colorsFor(slotIdx, slotTraceCount(slot)),
    );
    const forceCount = slots.filter(isForceSlot).length;
    const share = forceCount >= 2 && shareForceAxisPref;
    const groups: AxisGroup[] = [];
    let forceGroupIdx: number | null = null;
    for (const slot of slots) {
      if (share && isForceSlot(slot)) {
        if (forceGroupIdx === null) {
          forceGroupIdx = groups.length;
          groups.push({ slots: [slot], shared: true });
        } else {
          groups[forceGroupIdx]!.slots.push(slot);
        }
      } else {
        groups.push({ slots: [slot], shared: false });
      }
    }
    const bySlot = new Map<SlotId, number>();
    groups.forEach((g, idx) => g.slots.forEach((sl) => bySlot.set(sl, idx)));
    return {
      visibleSlots: slots,
      slotColors: colors,
      shareForceAxis: share,
      forceSelectedCount: forceCount,
      axisGroups: groups,
      axisIdxBySlot: bySlot,
    };
  }, [selectedSlots, shareForceAxisPref]);

  // Memoised: this maps over every point of every visible series to
  // build the Plotly trace arrays. It used to run on EVERY render —
  // not just when the data changed — so an unrelated state update
  // (a toggle, a hover) re-walked hundreds of thousands of samples.
  const traces = useMemo(() => visibleSlots.flatMap((slot, slotIdx) => {
    const palette = slotColors[slotIdx]!;
    const axisIdx = axisIdxBySlot.get(slot) ?? slotIdx;
    const yaxisRef = axisIdx === 0 ? "y" : `y${axisIdx + 1}`;
    const color = palette.traces[0] ?? palette.axis;

    // Jack slot: debug chips. No masking — surface the raw signals
    // so the operator can read the active-station integer off the
    // chart and tell us which numbers map to shaft / interjack.
    const j = jackOf(slot);
    if (j) {
      type Sub = { paramId: ParameterId; name: string };
      let subs: Sub[] = [];
      if (j === "activeStation") {
        subs = [{ paramId: "activeJackStation", name: "Station" }];
      }
      const unit = JACK_SLOTS[j].unit;
      const unitSuffix = unit ? ` (${unit})` : "";
      return subs.map((sub, traceIdx) => {
        const series = seriesById.get(sub.paramId);
        const points = series?.points ?? [];
        const ys = points.map((p) => p.v);
        const xs = xAxis === "chainage"
          ? points.map((p) => chainageAt(p.t))
          : points.map((p) => utcToLocalNaive(p.t, tz));
        const traceColor = palette.traces[traceIdx] ?? palette.axis;
        // Active station is a step integer — hv-shape line.
        return {
          x: xs,
          y: ys,
          type: "scatter",
          mode: "lines",
          name: `${sub.name}${unitSuffix}`,
          yaxis: yaxisRef,
          line: { color: traceColor, width: 1.4, shape: "hv" },
          connectgaps: false,
          hovertemplate: `${sub.name}: %{y}${unit ? ` ${unit}` : ""}<extra></extra>`,
        } as Record<string, unknown>;
      });
    }

    // Slurry slot: one line, sourced from /api/slurry/series.
    const sc = slurryOf(slot);
    if (sc) {
      const def = SLURRY_CHANNELS[sc];
      const points =
        slurryQuery.data?.series.find((s) => s.channel === sc)?.points ?? [];
      const xs = xAxis === "chainage"
        ? points.map((p) => chainageAt(p.t))
        : points.map((p) => utcToLocalNaive(p.t, tz));
      const out: Record<string, unknown>[] = [{
        x: xs,
        y: points.map((p) => p.v),
        type: "scattergl",
        mode: "lines",
        name: `${def.label} (${def.unit})`,
        yaxis: yaxisRef,
        line: { color, width: 1.4 },
        connectgaps: false,
        hovertemplate: `${def.label}: %{y:.5~g} ${def.unit}<extra></extra>`,
      } as Record<string, unknown>];
      // Manual readings for this channel: one marker trace per kind.
      const manual = manualQuery.data?.readings ?? [];
      for (const kind of Object.keys(SLURRY_MANUAL_KINDS) as SlurryManualKind[]) {
        if (manualSlotOf.get(kind) !== sc) continue;
        const rows = manual.filter((r: SlurryManualReading) => r.kind === kind);
        if (rows.length === 0) continue;
        const kdef = SLURRY_MANUAL_KINDS[kind];
        const isMarsh = kind === "marshFunnel";
        out.push({
          x: rows.map((r) => (xAxis === "chainage" ? chainageAt(r.ts) : utcToLocalNaive(r.ts, tz))),
          y: rows.map((r) => (isMarsh ? r.apparentViscosity : r.value)),
          type: "scatter",
          mode: "markers",
          name: isMarsh ? "Marsh funnel AV (manual)" : `${kdef.label} (manual)`,
          yaxis: yaxisRef,
          marker: {
            color: darken(color),
            size: 10,
            symbol: isMarsh ? "triangle-up" : "diamond",
            line: { color: "#fff", width: 1 },
          },
          customdata: rows.map((r) => {
            const head = isMarsh
              ? `Marsh funnel: ${r.value} s → AV ${r.apparentViscosity?.toFixed(1)} cP (ρ ${r.density} g/cm³)`
              : `${kdef.label} (manual): ${r.value} ${kdef.unit}`;
            return r.note ? `${head}<br>${escapeHover(r.note)}` : head;
          }),
          hovertemplate: "%{customdata}<extra></extra>",
        });
      }
      // The density entered with each Marsh funnel reading is a density
      // reading in its own right: plot it on the Density chip too.
      // Triangles (the Marsh symbol) tell it apart from mud-balance
      // density entries (diamonds).
      if (manualSlotOf.get("density") === sc) {
        const marsh = manual.filter(
          (r: SlurryManualReading) => r.kind === "marshFunnel" && r.density != null,
        );
        if (marsh.length > 0) {
          out.push({
            x: marsh.map((r) => (xAxis === "chainage" ? chainageAt(r.ts) : utcToLocalNaive(r.ts, tz))),
            y: marsh.map((r) => r.density),
            type: "scatter",
            mode: "markers",
            name: "Density (Marsh, manual)",
            yaxis: yaxisRef,
            marker: {
              color: darken(color),
              size: 10,
              symbol: "triangle-up",
              line: { color: "#fff", width: 1 },
            },
            customdata: marsh.map((r) => {
              const head = `Density (with Marsh funnel ${r.value} s): ${r.density} g/cm³`;
              return r.note ? `${head}<br>${escapeHover(r.note)}` : head;
            }),
            hovertemplate: "%{customdata}<extra></extra>",
          });
        }
      }
      return out;
    }

    // Derived slot: one trace, sourced from /api/secondary.
    const d = derivedOf(slot);
    if (d) {
      const def = DERIVED_SLOTS[d];
      const resp = derivedQuery.data?.get(def.analysis);
      const points = resp?.points ?? [];
      const xs: (string | number | null)[] =
        xAxis === "chainage"
          ? points.map((p) => p.x)
          : points.map((p) => (p.t ? utcToLocalNaive(p.t, tz) : null));
      const ys = points.map((p) => p.y);
      const unitSuffix = def.unit ? ` ${def.unit}` : "";
      // Resolved Weight is a smooth function of chainage, so it reads as
      // a line; the other derived analyses are discrete per-sample dots.
      const derivedTraces: Record<string, unknown>[] = [
        d === "resolvedWeight"
          ? {
              x: xs,
              y: ys,
              type: "scatter",
              mode: "lines",
              name: def.unit ? `${def.traceLabel} (${def.unit})` : def.traceLabel,
              yaxis: yaxisRef,
              line: { color, width: 1.4 },
              connectgaps: false,
              hovertemplate: `${def.label}: %{y:.3f}${unitSuffix}<extra></extra>`,
            }
          : {
              x: xs,
              y: ys,
              type: "scattergl",
              mode: "markers",
              name: def.unit ? `${def.traceLabel} (${def.unit})` : def.traceLabel,
              yaxis: yaxisRef,
              marker: { color, size: 4, opacity: 0.55 },
              hovertemplate: `${def.label}: %{y:.3f}${unitSuffix}<extra></extra>`,
            },
      ];

      // Bulking Factor: overlay a weighted moving-average line. The
      // line is broken across long mining gaps so a multi-hour stoppage
      // doesn't draw one segment spanning an empty stretch of plot.
      if (d === "bulkingFactor" && ma.enabled && points.length > 1) {
        const maY = bulkingMovingAverage(points, ma);
        const lineX: (string | number | null)[] = [];
        const lineY: (number | null)[] = [];
        for (let k = 0; k < points.length; k++) {
          lineX.push(xs[k] ?? null);
          lineY.push(maY[k] ?? null);
          const cur = points[k]!.t;
          const next = points[k + 1]?.t;
          if (
            cur &&
            next &&
            new Date(next).getTime() - new Date(cur).getTime() >
              MA_GAP_BREAK_MS
          ) {
            lineX.push(null);
            lineY.push(null);
          }
        }
        derivedTraces.push({
          x: lineX,
          y: lineY,
          type: "scatter",
          mode: "lines",
          name: "Bulking Factor (smoothed)",
          yaxis: yaxisRef,
          line: { color, width: 2 },
          connectgaps: false,
          hovertemplate: "Smoothed BF: %{y:.3f}<extra></extra>",
        });
      }
      return derivedTraces;
    }

    // Regular slot: 1 (single) or N (group) traces from /api/series.
    const paramIds = slotParamIds(slot);
    const traceLabels = isGroupSlot(slot)
      ? [...PARAM_GROUPS[groupOf(slot)!].traceLabels]
      : [PARAMETERS[slot as ParameterId].shortName];
    return paramIds.flatMap((paramId, traceIdx) => {
      const param = PARAMETERS[paramId];
      const series = seriesById.get(paramId);
      const points = series?.points ?? [];
      const traceColor = palette.traces[traceIdx] ?? palette.axis;
      let xs: (string | number | null)[];
      if (xAxis === "chainage") {
        xs = points.map((p) => chainageAt(p.t));
      } else {
        xs = points.map((p) => utcToLocalNaive(p.t, tz));
      }
      const traceName = traceLabels[traceIdx] ?? param.shortName;
      const hovertemplate = `${param.displayName}: %{y:.${param.precision}f} ${param.unit}<extra></extra>`;

      // Ram-stage correction (Jacking Force only): multiply each sample
      // by its stage ratio. Samples corrected by a ratio ≠ 1 split off
      // into a separate "Corrected Jacking Force" trace drawn as crosses.
      //
      // SINGLE RAM-STAGE APPLICATION POINT (3/3). /api/series returns
      // the rolling-filtered RAW JF (no server-side ram-stage); the
      // multiplication happens here, once per sample. Each chainage
      // point ends up in exactly one of normalY / correctedY (the
      // other is null at the same index), so Plotly never draws the
      // same sample twice. See ramStageRatio's docstring for the full
      // single-application invariant.
      if (paramId === "jackingForce" && ramStage.enabled) {
        const normalY: (number | null)[] = [];
        const correctedY: (number | null)[] = [];
        for (const p of points) {
          const ratio = ramStageRatio(
            chainageAt(p.t),
            pipeChanges,
            ramStage,
          );
          const v = p.v == null ? null : p.v * ratio;
          if (ratio !== 1) { correctedY.push(v); normalY.push(null); }
          else { normalY.push(v); correctedY.push(null); }
        }
        return [
          {
            x: xs,
            y: normalY,
            type: "scattergl",
            mode: "markers",
            name: `${traceName} (${param.unit})`,
            yaxis: yaxisRef,
            marker: { color: traceColor, size: 4, opacity: 0.55 },
            hovertemplate,
          } as Record<string, unknown>,
          {
            x: xs,
            y: correctedY,
            type: "scattergl",
            mode: "markers",
            name: `Corrected Jacking Force (${param.unit})`,
            yaxis: yaxisRef,
            marker: { color: traceColor, size: 6, symbol: "x", opacity: 0.85 },
            hovertemplate: `Corrected Jacking Force: %{y:.${param.precision}f} ${param.unit}<extra></extra>`,
          } as Record<string, unknown>,
        ];
      }

      const ys = points.map((p) => p.v);
      if (DOT_PARAMS.has(paramId)) {
        return [{
          x: xs,
          y: ys,
          type: "scattergl",
          mode: "markers",
          name: `${traceName} (${param.unit})`,
          yaxis: yaxisRef,
          marker: { color: traceColor, size: 4, opacity: 0.55 },
          hovertemplate,
        } as Record<string, unknown>];
      }
      // WebGL lines: SVG `scatter` paths get very slow past a few
      // thousand points (7d/Drive ranges), and these are the largest
      // traces on the page. scattergl draws the same solid line.
      return [{
        x: xs,
        y: ys,
        type: "scattergl",
        mode: "lines",
        name: `${traceName} (${param.unit})`,
        yaxis: yaxisRef,
        line: { color: traceColor, width: 1.4 },
        connectgaps: false,
        hovertemplate,
      } as Record<string, unknown>];
    });
  }), [
    visibleSlots,
    slotColors,
    axisIdxBySlot,
    seriesById,
    derivedQuery.data,
    slurryQuery.data,
    manualQuery.data,
    manualSlotOf,
    xAxis,
    tz,
    chainageAt,
    pipeChanges,
    ramStage,
    ma,
  ]);

  // Axis layout: y1 anchored left, y2 anchored right, y3 free-left at
  // position 0, y4 free-right at position 1. Shrink the x-axis domain
  // 8% on each side that hosts a free axis. Now keyed off axisGroups
  // rather than visibleSlots, since a shared force axis can collapse
  // multiple selected chips into one rendered axis.
  const n = axisGroups.length;
  const hasFreeLeft = n >= 3;
  const hasFreeRight = n >= 4;
  const leftDomain = hasFreeLeft ? 0.08 : 0;
  const rightDomain = hasFreeRight ? 0.92 : 1;

  const yAxes: Record<string, Record<string, unknown>> = {};
  axisGroups.forEach((group, idx) => {
    const key = idx === 0 ? "yaxis" : `yaxis${idx + 1}`;
    let titleText: string;
    let axisColor: string;
    if (group.shared) {
      // Shared force axis. Title is unit-only ("Force (kN)") and the
      // colour is plain black — individual trace colours live in the
      // legend, not on the axis ticks.
      titleText = "Force (kN)";
      axisColor = "#000";
    } else {
      const slot = group.slots[0]!;
      const palette = slotColors[visibleSlots.indexOf(slot)]!;
      const unitPart = slotUnit(slot) ? ` (${slotUnit(slot)})` : "";
      titleText = `${slotShortLabel(slot)}${unitPart}`;
      axisColor = palette.axis;
    }
    const base: Record<string, unknown> = {
      title: { text: titleText, font: { color: axisColor, size: 11 } },
      tickfont: { color: axisColor, size: 10 },
      color: axisColor,
      // Suppress the y=0 horizontal "zero line" Plotly's `simple_white`
      // template draws on every axis by default. PlotCard merges this
      // off for `yaxis` via plotlyBaseLayout, but the per-slot
      // yaxis2/3/4 configs here are spread untouched (no inheritance
      // from yaxis) so each one needs the override explicitly.
      zeroline: false,
      showline: false,
    };
    if (idx === 0) {
      base.side = "left";
    } else if (idx === 1) {
      base.side = "right";
      base.overlaying = "y";
    } else if (idx === 2) {
      base.side = "left";
      base.overlaying = "y";
      base.anchor = "free";
      base.position = 0;
    } else if (idx === 3) {
      base.side = "right";
      base.overlaying = "y";
      base.anchor = "free";
      base.position = 1;
    }
    yAxes[key] = base;
  });

  const xaxisLayout: Record<string, unknown> = {
    domain: [leftDomain, rightDomain],
    ...(xAxis === "time"
      ? { tickformat: "%-d %b", hoverformat: "%-d %b %Y · %H:%M", title: { text: "" } }
      : { title: { text: "Chainage (m)" } }),
  };

  // Pipe-change overlay. Reads from the pipeChanges list already
  // attached server-side to /api/series, so this costs no extra fetch.
  const pipeOverlayActive = showPipeChanges;
  const { shapes: pipeShapes, annotations: pipeAnnotations } = useMemo(() => {
    if (!pipeOverlayActive) {
      return { shapes: [] as Record<string, unknown>[], annotations: [] as Record<string, unknown>[] };
    }
    return buildPipeOverlay(pipeChanges, xAxis, tz);
  }, [pipeOverlayActive, pipeChanges, xAxis, tz]);

  const isLoading =
    seriesQuery.isLoading ||
    seriesQuery.isPlaceholderData ||
    derivedQuery.isFetching ||
    slurryQuery.isFetching;
  const pipeCount = pipeChanges.length;
  // When the server auto-downsampled a raw request (wide windows only),
  // say so — the operator should know the trace is bucketed averages,
  // not individual samples.
  const appliedAgg = seriesQuery.data?.applied.aggregation;
  const dsSuffix =
    pp.aggregation === "raw" && appliedAgg && appliedAgg !== "raw"
      ? ` · downsampled to ${AGG_LABELS[appliedAgg] ?? appliedAgg}`
      : "";
  const pipeSuffix = pipeOverlayActive
    ? ` · ${pipeCount} pipe ${pipeCount === 1 ? "change" : "changes"} in window`
    : "";
  const subtitle =
    range === "custom" && !windowReady
      ? chainageWindow
        ? "Enter a start chainage to load a custom window"
        : "Pick a start date to load a custom window"
      : isLoading
      ? "loading…"
      : visibleSlots.length === 0
        ? "Select one or more parameters below"
        : `${visibleSlots.length} of ${MAX_SLOTS} parameters · ${xAxis === "chainage" ? "vs chainage" : "vs time"}${pipeSuffix}${dsSuffix}`;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-end justify-between pt-1">
        <div>
          <h2 className="text-[13px] font-semibold tracking-[0.18em] uppercase text-text">
            {slurryView ? "Slurry monitoring" : "Drive parameters"}
          </h2>
          <div className="text-[11px] text-muted mt-1">
            Tap to overlay; up to {MAX_SLOTS} parameters.
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Segmented
          value={xAxis}
          onChange={setXAxisTracked}
          options={[
            { id: "time",     label: "Time" },
            { id: "chainage", label: "Chainage" },
          ]}
        />
        <PipeChangeToggle value={showPipeChanges} onChange={setShowPipeChanges} />
        {!slurryView && (
          <ShareForceAxisToggle
            value={shareForceAxisPref}
            onChange={setShareForceAxisPref}
            enabled={forceSelectedCount >= 2}
          />
        )}
        <div className="flex-1" />
        <RangeToggle range={range} onChange={setRangeTracked} />
      </div>

      {range === "custom" && (
        <CustomWindowInputs
          xAxis={xAxis}
          fromDate={customFromDate}
          toDate={customToDate}
          fromCh={customFromCh}
          toCh={customToCh}
          onFromDate={setCustomFrom}
          onToDate={setCustomTo}
          onFromCh={setCustomFromCh}
          onToCh={setCustomToCh}
        />
      )}

      <ParameterChips
        categories={visibleCategories}
        selected={visibleSlots}
        slotColors={slotColors}
        atCap={selectedSlots.length >= MAX_SLOTS}
        onToggle={toggleSlot}
      />

      <PlotCard
        title={
          visibleSlots.map((s) => slotShortLabel(s)).join("  ·  ") ||
          "No parameters selected"
        }
        subtitle={subtitle}
        data={traces as never}
        height={460}
        // Slot order + axis + range in the revision so any of those
        // changes resets framing. Within a stable selection the user's
        // zoom/pan still survives refetches.
        uirevision={`drive-multi-${visibleSlots.join(",")}-${xAxis}-${range}-${range === "custom" ? (chainageWindow ? `${customFromCh}~${customToCh}-` : `${customFromDate}~${customToDate}-`) : ""}${pipeOverlayActive ? "p1" : "p0"}-${shareForceAxis ? "sf1" : "sf0"}`}
        layout={{
          // Extra top room so the pipe-overlay labels (yref:paper, y:1)
          // have space above the plot area without being clipped or
          // overlapping the card title.
          margin: { l: 56, r: 56, t: 30, b: 36 },
          xaxis: xaxisLayout,
          ...yAxes,
          shapes: pipeShapes as never,
          annotations: pipeAnnotations as never,
          // Legend lists every trace (singles + each member of a
          // group + each derived analysis). Top-LEFT, just inside the
          // plot's left boundary. When a third axis is active that
          // axis sits in the 0..leftDomain strip outside the plot, so
          // we anchor the legend at `leftDomain + 0.01` to stay clear
          // of its tick labels instead of overlapping them.
          showlegend: visibleSlots.length > 0,
          legend: {
            x: leftDomain + 0.01,
            y: 0.99,
            xanchor: "left",
            yanchor: "top",
            bgcolor: "rgba(255,255,255,0.88)",
            bordercolor: "rgba(0,0,0,0.08)",
            borderwidth: 1,
            font: { size: 10, color: "#374151" },
          },
        }}
      />

      <ParameterDefinitions
        visibleSlots={visibleSlots}
        slotColors={slotColors}
        inclineEnabled={drive.data?.incline?.enabled === true}
      />
    </div>
  );
}

/**
 * Reference panel under the plot listing what each active chip
 * means. Single params get a one-line definition; group chips list
 * their member traces; derived chips show their equation, the
 * variables, and any operator caveats. Wording (where it applies)
 * is from the source thesis Table 3.3.
 *
 * Wrapped in the same `card` chrome as the plot so it reads as a
 * peer section rather than a tagged-on text block. Rendered *after*
 * the PlotCard, so adding or removing chips changes the panel below
 * the chart only — the chart's vertical position never moves. The
 * whole section is omitted when no chips are active, so the page is
 * just plot in that case (no reserved empty card).
 *
 * Single column by design so the equations + variable tables stay
 * aligned at any viewport width and the eye doesn't have to track
 * two columns when toggling chips.
 */
function ParameterDefinitions({
  visibleSlots,
  slotColors,
  inclineEnabled,
}: {
  visibleSlots: SlotId[];
  slotColors: { traces: string[]; axis: string }[];
  inclineEnabled: boolean;
}) {
  if (visibleSlots.length === 0) return null;
  return (
    <section className="card px-6 py-5 max-md:px-4 max-md:py-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-dim mb-4">
        Parameter definitions
      </div>
      {/*
        At lg+ (1024 px, matches the DesktopApp / MobileApp split in
        useMediaQuery.ts) the panel lays out as a fixed 4-column grid —
        one column per axis slot, MAX_SLOTS = 4 always. Active blocks
        fill columns left to right; unfilled columns are simply blank.
        Each column has a hairline left border so two adjacent blocks
        read as separated panels rather than one long row of text.

        Below lg the panel falls back to a single column with hairline
        rows (`divide-y`), matching the previous mobile behaviour and
        the rest of the page's stacked layout on phones / tablets.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-4 max-lg:divide-y max-lg:divide-border1/60">
        {visibleSlots.map((slot, idx) => {
          const palette = slotColors[idx]!;
          return (
            <div
              key={slot}
              className={clsx(
                // Mobile / tablet portrait: vertical padding, flush at ends.
                "max-lg:py-3.5 max-lg:first:pt-0 max-lg:last:pb-0",
                // Desktop / tablet landscape: horizontal padding, flush at ends.
                "lg:px-5 lg:first:pl-0 lg:last:pr-0",
                // Left hairline between columns at lg+. First column has none.
                idx > 0 && "lg:border-l lg:border-border1/60",
              )}
            >
              <DefBlock
                slot={slot}
                axisColor={palette.axis}
                traceColors={palette.traces}
                inclineEnabled={inclineEnabled}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function DefBlock({
  slot,
  axisColor,
  traceColors,
  inclineEnabled,
}: {
  slot: SlotId;
  axisColor: string;
  traceColors: string[];
  inclineEnabled: boolean;
}) {
  const derivedId = derivedOf(slot);
  const groupId = groupOf(slot);
  const jackId = jackOf(slot);
  const slurryId = slurryOf(slot);
  return (
    <div className="py-3.5 first:pt-0 last:pb-0">
      <div
        className="text-[10.5px] uppercase tracking-[0.18em] font-semibold mb-1"
        style={{ color: axisColor }}
      >
        {slotShortLabel(slot)}
      </div>
      {slurryId
        ? (
          <p className="text-[11.5px] text-muted leading-relaxed">
            {SLURRY_CHANNELS[slurryId].description}
          </p>
        )
        : jackId
        ? <p className="text-[11.5px] text-muted leading-relaxed">{JACK_DEFS[jackId]}</p>
        : derivedId
          ? <DerivedBody def={derivedDefFor(derivedId, inclineEnabled)} />
          : groupId
            ? <GroupBody def={GROUP_DEFS[groupId]} traceColors={traceColors} />
            : (
              <p className="text-[11.5px] text-muted leading-relaxed">
                {SINGLE_DEFS[slot as ParameterId] ?? ""}
              </p>
            )}
    </div>
  );
}

function GroupBody({
  def,
  traceColors,
}: {
  def: { short?: string; traces?: { label: string; meaning: string }[] };
  traceColors: string[];
}) {
  if (def.short) {
    return <p className="text-[11.5px] text-muted leading-relaxed">{def.short}</p>;
  }
  if (!def.traces) return null;
  // Two-column grid: trace name in its shade, definition in muted body
  // text. No bullet markers — the colour-tinted name is the marker.
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-[11.5px] leading-relaxed">
      {def.traces.map((t, i) => (
        <Fragment key={t.label}>
          <dt
            className="font-medium"
            style={{ color: traceColors[i] ?? undefined }}
          >
            {t.label}
          </dt>
          <dd className="text-muted">{t.meaning}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

function DerivedBody({
  def,
}: {
  def: { equation: string; variables: { sym: string; meaning: string }[]; notes?: string[] };
}) {
  return (
    <div>
      <div className="font-mono text-[12.5px] text-text my-1.5">
        {renderMath(def.equation)}
      </div>
      {/* Symbol → meaning grid. No bullets. Symbol in mono, meaning in
          body text, columns aligned so the symbols form a tidy gutter. */}
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-[11px] leading-relaxed mt-1">
        {def.variables.map((v) => (
          <Fragment key={v.sym}>
            <dt className="font-mono text-text">{renderMath(v.sym)}</dt>
            <dd className="text-muted">{v.meaning}</dd>
          </Fragment>
        ))}
      </dl>
      {def.notes?.map((n, i) => (
        <p
          key={i}
          className="text-[11px] text-muted leading-relaxed mt-2"
        >
          {n}
        </p>
      ))}
    </div>
  );
}

function ParameterChips({
  categories,
  selected,
  slotColors,
  atCap,
  onToggle,
}: {
  categories: Array<{ label: string; slots: SlotId[] }>;
  selected: SlotId[];
  slotColors: { traces: string[]; axis: string }[];
  atCap: boolean;
  onToggle: (slot: SlotId) => void;
}) {
  // Map the active slot id → the family colour that the plot uses for
  // its axis. Lets each active chip render in the colour of its data.
  const axisBySlot = new Map<SlotId, string>();
  selected.forEach((slot, idx) => {
    const c = slotColors[idx];
    if (c) axisBySlot.set(slot, c.axis);
  });

  const renderChip = (slot: SlotId) => {
    const isSelected = selected.includes(slot);
    const axisColor = axisBySlot.get(slot);
    const disabled = !isSelected && atCap;
    const groupId = groupOf(slot);
    const derivedId = derivedOf(slot);
    const jackId = jackOf(slot);
    const slurryId = slurryOf(slot);
    const tooltipName = slurryId
      ? `${SLURRY_CHANNELS[slurryId].label} (${SLURRY_CHANNELS[slurryId].unit})`
      : jackId
      ? `${JACK_SLOTS[jackId].label} (${JACK_SLOTS[jackId].unit})`
      : derivedId
        ? DERIVED_SLOTS[derivedId].label
        : groupId
          ? PARAM_GROUPS[groupId].traceLabels.join(" + ") +
            ` (${PARAM_GROUPS[groupId].unit})`
          : PARAMETERS[slot as ParameterId].displayName;
    return (
      <button
        key={slot}
        type="button"
        onClick={() => onToggle(slot)}
        disabled={disabled}
        title={
          disabled
            ? `Already showing ${selected.length} axes; toggle one off first`
            : isSelected
              ? `Hide ${tooltipName}`
              : `Show ${tooltipName}`
        }
        className={clsx(
          "inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] tracking-wider uppercase whitespace-nowrap transition-colors flex-shrink-0",
          isSelected
            ? "border-transparent text-white"
            : disabled
              ? "bg-surface2 border-border1 text-dim cursor-not-allowed opacity-60"
              : "bg-surface2 border-border1 text-muted hover:bg-surface3 hover:text-text",
        )}
        style={isSelected && axisColor ? { backgroundColor: axisColor } : undefined}
      >
        <span
          aria-hidden="true"
          className="h-1.5 w-1.5 rounded-full"
          style={{
            backgroundColor: isSelected
              ? "rgba(255,255,255,0.85)"
              : axisColor ?? "currentColor",
            opacity: isSelected ? 1 : 0.5,
          }}
        />
        {slotShortLabel(slot)}
      </button>
    );
  };

  // Desktop: three columns separated by vertical lines. Width is
  // proportional to a chip-width estimate (label chars + a baseline
  // for the padding / icon / inter-chip gap), summed across the
  // category — NOT to chip count. Using count gave Secondary roughly
  // the same column width as Status even though "Cutterhead Force"
  // alone is wider than three Status chips, so Secondary wrapped to
  // three rows while Measured had empty space. The estimate doesn't
  // need to match pixels exactly; flex grows from these as a ratio,
  // so what matters is that a longer label counts more.
  //
  // Mobile stacks vertically and drops the separators so chips stay
  // touchable.
  const widthWeight = (slots: SlotId[]) =>
    slots.reduce((sum, slot) => sum + slotShortLabel(slot).length + 5, 0);

  return (
    <div className="flex items-stretch gap-3 max-md:flex-col max-md:gap-2">
      {categories.map((cat, i) => (
        <Fragment key={cat.label}>
          {i > 0 && (
            <div
              aria-hidden="true"
              className="self-stretch w-px bg-border1 max-md:hidden"
            />
          )}
          <div
            className="min-w-0"
            style={{ flex: `${widthWeight(cat.slots)} 1 0%` }}
          >
            <div className="text-[9px] uppercase tracking-[0.18em] text-dim mb-1.5">
              {cat.label}
            </div>
            <div className="flex flex-wrap gap-1.5 max-md:flex-nowrap max-md:overflow-x-auto max-md:-mx-3 max-md:px-3 max-md:pb-1">
              {cat.slots.map(renderChip)}
            </div>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { id: T; label: string }[];
}) {
  return (
    <div className="inline-flex bg-surface2 border border-border1 rounded overflow-hidden">
      {options.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          className={clsx(
            "px-3 py-1.5 text-[11px] tracking-wider uppercase transition-colors",
            value === o.id ? "bg-wb text-white" : "text-muted hover:bg-surface3 hover:text-text",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function PipeChangeToggle({
  value,
  onChange,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={clsx(
        "inline-flex items-center gap-2 px-3 py-1.5 rounded border text-[11px] tracking-wider uppercase transition-colors",
        value
          ? "bg-wb text-white border-transparent"
          : "bg-surface2 border-border1 text-muted hover:bg-surface3 hover:text-text",
      )}
      title="Toggle pipe-change overlay"
    >
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: value ? "rgba(255,255,255,0.85)" : PIPE_CHANGE_COLOR }}
      />
      Pipe change
    </button>
  );
}

/** Toggle whether 2+ selected forces share a single "Force (kN)" axis.
 *  Greyed out (disabled) until at least two forces are selected, since
 *  sharing is meaningless with one. */
function ShareForceAxisToggle({
  value,
  onChange,
  enabled,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  enabled: boolean;
}) {
  const on = enabled && value;
  return (
    <button
      type="button"
      disabled={!enabled}
      onClick={() => onChange(!value)}
      className={clsx(
        "inline-flex items-center gap-2 px-3 py-1.5 rounded border text-[11px] tracking-wider uppercase transition-colors",
        !enabled
          ? "bg-surface2 border-border1 text-dim opacity-50 cursor-not-allowed"
          : on
            ? "bg-wb text-white border-transparent"
            : "bg-surface2 border-border1 text-muted hover:bg-surface3 hover:text-text",
      )}
      title={
        enabled
          ? "Share a single Force (kN) axis across the selected forces"
          : "Select two or more forces to share an axis"
      }
    >
      <span
        aria-hidden="true"
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: on ? "rgba(255,255,255,0.85)" : "#000" }}
      />
      Share Force axis
    </button>
  );
}

// ─── Bulking-factor moving average ────────────────────────────────
// Linear-weighted (triangular) moving average of the Bulking Factor
// trace, windowed by chainage (metres) and looking both forward and
// back from each point. Each neighbour's weight falls off linearly
// with its chainage distance from the centre point.
/** Break the smoothed line across mining gaps longer than this. */
const MA_GAP_BREAK_MS = 30 * 60 * 1000;

/**
 * Weighted moving average of `points`, one value per input point.
 * Windowed by chainage (point.x, metres). Assumes points are ordered
 * by non-decreasing chainage — guaranteed by the chainage filter.
 */
function bulkingMovingAverage(
  points: AnalysisPoint[],
  s: BulkingMaSettings,
): (number | null)[] {
  const fwd = Math.max(s.forwardM, 0);
  const back = Math.max(s.backM, 0);
  const n = points.length;
  const out: (number | null)[] = new Array(n).fill(null);
  if (fwd <= 0 && back <= 0) return out;
  let lo = 0;
  let hi = 0;
  for (let i = 0; i < n; i++) {
    const ci = points[i]!.x;
    while (lo < n && points[lo]!.x < ci - back) lo++;
    while (hi < n && points[hi]!.x <= ci + fwd) hi++;
    let wsum = 0;
    let vsum = 0;
    for (let j = lo; j < hi; j++) {
      const y = points[j]!.y;
      if (y == null || !Number.isFinite(y)) continue;
      const offset = points[j]!.x - ci;
      const side = offset >= 0 ? fwd : back;
      const norm = side > 0 ? Math.min(1, Math.abs(offset) / side) : 0;
      const w = 1 - norm; // linear (triangular) weighting
      wsum += w;
      vsum += w * y;
    }
    out[i] = wsum > 0 ? vsum / wsum : null;
  }
  return out;
}

/**
 * Above this many pipes in the window, the overlay draws lines but drops
 * the labels. On the Drive range a long bore puts hundreds of pipes on
 * screen; every one contributed a shape AND an annotation, which Plotly
 * lays out individually — and the labels overlapped into an unreadable
 * band across the top of the chart anyway.
 */
const PIPE_LABEL_LIMIT = 60;

function buildPipeOverlay(
  changes: PipeChange[],
  xAxis: XAxis,
  tz: string,
): { shapes: Record<string, unknown>[]; annotations: Record<string, unknown>[] } {
  const shapes: Record<string, unknown>[] = [];
  const annotations: Record<string, unknown>[] = [];
  const showLabels = changes.length <= PIPE_LABEL_LIMIT;
  // The label is the operator-set name when present, otherwise just the
  // pipe number. The "Pipe " prefix is dropped so a row of labels above
  // the chart fits in the small top margin without clipping the title.
  const labelFor = (pc: PipeChange): string =>
    pc.name && pc.name.trim() !== "" ? pc.name.trim() : String(pc.pipeIndex);

  if (xAxis === "chainage") {
    // One marker per pipe-to-pipe boundary, drawn at the next pipe's
    // install chainage. The boundary line is labelled with pipe N (the
    // pipe that starts here).
    for (const pc of changes) {
      if (pc.pipeIndex < 2) continue; // nothing before pipe 1
      shapes.push(verticalLine(pc.chainageM, PIPE_CHANGE_COLOR));
      if (showLabels) {
        annotations.push(
          verticalLabel(pc.chainageM, labelFor(pc), PIPE_CHANGE_COLOR),
        );
      }
    }
    return { shapes, annotations };
  }

  // Time axis: green start at startTs (push begins), red end at endTs
  // (push ends). Both flags carry the same label (pipe N's name or
  // number) — the colour distinguishes start from end.
  for (const pc of changes) {
    const label = labelFor(pc);
    if (pc.startTs) {
      const x = utcToLocalNaive(pc.startTs, tz);
      if (x) {
        shapes.push(verticalLine(x, PIPE_START_COLOR));
        if (showLabels) annotations.push(verticalLabel(x, label, PIPE_START_COLOR));
      }
    }
    if (pc.endTs) {
      const x = utcToLocalNaive(pc.endTs, tz);
      if (x) {
        shapes.push(verticalLine(x, PIPE_END_COLOR));
        if (showLabels) annotations.push(verticalLabel(x, label, PIPE_END_COLOR));
      }
    }
  }
  return { shapes, annotations };
}

function verticalLine(x: string | number, color: string): Record<string, unknown> {
  return {
    type: "line",
    xref: "x",
    yref: "paper",
    x0: x,
    x1: x,
    y0: 0,
    y1: 1,
    line: { color, width: 1, dash: "dash" },
    layer: "below",
  };
}

function verticalLabel(
  x: string | number,
  text: string,
  color: string,
): Record<string, unknown> {
  return {
    xref: "x",
    yref: "paper",
    x,
    y: 1,
    yanchor: "bottom",
    xanchor: "center",
    text,
    showarrow: false,
    align: "center",
    font: { color, size: 9 },
    bgcolor: "rgba(255,255,255,0.85)",
  };
}

/**
 * Start–end selector for the Custom range, rendered as one combined
 * control (like a holiday-booking from–to picker) rather than two
 * separate full inputs. Follows the x-axis mode: dates on the time
 * axis (wall-clock days in the drive's timezone, converted to UTC at
 * the API boundary), metres on the chainage axis. A blank end means
 * "up to now / current chainage" — that window live-refreshes like
 * the relative ranges; a date-bounded window is fetched once and
 * never refetched.
 */
function CustomWindowInputs({
  xAxis,
  fromDate,
  toDate,
  fromCh,
  toCh,
  onFromDate,
  onToDate,
  onFromCh,
  onToCh,
}: {
  xAxis: XAxis;
  fromDate: string;
  toDate: string;
  fromCh: string;
  toCh: string;
  onFromDate: (v: string) => void;
  onToDate: (v: string) => void;
  onFromCh: (v: string) => void;
  onToCh: (v: string) => void;
}) {
  const isCh = xAxis === "chainage";
  const inputCls =
    "bg-transparent px-1.5 py-1 text-[11px] text-text focus:outline-none";
  return (
    <div className="flex flex-wrap items-center justify-end gap-2 -mt-2 text-[10px] uppercase tracking-wider text-muted">
      <div className="inline-flex items-center bg-surface2 border border-border1 rounded px-0.5">
        {isCh ? (
          <>
            <input
              type="number"
              inputMode="decimal"
              placeholder="start"
              value={fromCh}
              onChange={(e) => onFromCh(e.target.value)}
              className={clsx(inputCls, "w-20")}
            />
            <span className="text-dim px-0.5">–</span>
            <input
              type="number"
              inputMode="decimal"
              placeholder="end"
              value={toCh}
              onChange={(e) => onToCh(e.target.value)}
              className={clsx(inputCls, "w-20")}
            />
            <span className="pr-1.5 pl-0.5 text-dim">m</span>
          </>
        ) : (
          <>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => onFromDate(e.target.value)}
              className={inputCls}
            />
            <span className="text-dim px-0.5">–</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => onToDate(e.target.value)}
              className={inputCls}
            />
          </>
        )}
      </div>
    </div>
  );
}

function RangeToggle({ range, onChange }: { range: Range; onChange: (r: Range) => void }) {
  return (
    <div className="inline-flex bg-surface2 border border-border1 rounded overflow-hidden">
      {RANGES.map((r) => (
        <button
          key={r.id}
          onClick={() => onChange(r.id)}
          className={clsx(
            "px-3 py-1.5 text-[11px] tracking-wider uppercase transition-colors",
            range === r.id ? "bg-wb text-white" : "text-muted hover:bg-surface3 hover:text-text",
          )}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}
